import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaDiscord, FaCheck, FaExclamationTriangle, FaSpinner } from 'react-icons/fa';
import discordService from '../../services/discordService';
import adminService from '../../services/adminService';
import './AuthCallback.css';

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [userData, setUserData] = useState(null);
  const [oauthIntent, setOauthIntent] = useState('account');
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const handleAuthCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      if (error) {
        setStatus('error');
        setMessage(`Erro na autenticação: ${error}`);
        return;
      }

      if (!code) {
        setStatus('error');
        setMessage('Código de autorização não encontrado');
        return;
      }

      const { valid, intent: rawIntent } = discordService.validateState(state);
      const intent =
        rawIntent === 'vip'
          ? 'vip'
          : rawIntent === 'admin'
            ? 'admin'
            : rawIntent === 'economia'
              ? 'economia'
              : 'account';
      setOauthIntent(intent);

      if (!valid) {
        setStatus('error');
        setMessage('Estado de autenticação inválido. Por favor, tente novamente.');
        return;
      }

      try {
        setStatus('loading');
        setMessage('Conectando sua conta Discord...');
        const result = await discordService.loginIdentity(code);

        if (result.success) {
          discordService.setPlayerAuth(result.user, result.accessToken || null);
          if (intent === 'admin' && result.accessToken) {
            adminService.setAdminAuth(result.user, result.accessToken);
          }
          window.dispatchEvent(new Event('nexus:auth-changed'));
          setUserData(result.user);
          setStatus('success');
          if (intent === 'vip') {
            setMessage('Discord conectado! Voltando aos planos VIP...');
            setTimeout(() => navigate('/vip?checkout=ready'), 800);
          } else if (intent === 'admin') {
            setMessage('Discord conectado! Abrindo o painel admin...');
            setTimeout(() => navigate('/admin'), 800);
          } else if (intent === 'economia') {
            setMessage('Discord conectado! Abrindo o Nexus Pay...');
            setTimeout(() => navigate('/economia'), 800);
          } else {
            setMessage('Discord conectado! Abrindo sua área...');
            setTimeout(() => navigate('/conta'), 800);
          }
        } else {
          setStatus('error');
          setMessage(result.error || 'Falha ao conectar Discord');
        }
      } catch (err) {
        console.error('Erro no callback de autenticação:', err);
        setStatus('error');
        setMessage('Ocorreu um erro durante a verificação. Por favor, tente novamente.');
      }
    };

    handleAuthCallback();
  }, [searchParams, navigate]);

  const successCopy =
    oauthIntent === 'vip'
      ? 'Sua conta Discord foi vinculada. Voltando aos planos VIP...'
      : oauthIntent === 'admin'
        ? 'Sua conta Discord foi vinculada. Abrindo o painel de gerenciamento...'
        : oauthIntent === 'economia'
          ? 'Sua conta Discord foi vinculada. Abrindo o Nexus Pay...'
          : 'Sua conta Discord foi vinculada. Abrindo a área do jogador...';

  const handleTryAgain = () => {
    if (oauthIntent === 'vip') navigate('/vip');
    else if (oauthIntent === 'admin') navigate('/admin');
    else if (oauthIntent === 'economia') navigate('/economia');
    else navigate('/conta');
  };

  return (
    <div className={`auth-cb auth-cb--${status}`}>
      <div className="auth-cb__glow" aria-hidden="true" />
      <div className="auth-cb__blocks" aria-hidden="true" />

      <main className="auth-cb__content">
        <p className="auth-cb__kicker">
          {status === 'success'
            ? 'Tudo certo'
            : status === 'error'
              ? 'Algo deu errado'
              : 'Autenticação'}
        </p>
        <p className="auth-cb__brand">NEXUS</p>

        <div className="auth-cb__icon" aria-hidden="true">
          {status === 'loading' && <FaSpinner className="auth-cb__spin" />}
          {status === 'success' && <FaCheck />}
          {status === 'error' && <FaExclamationTriangle />}
          {status !== 'loading' && status !== 'success' && status !== 'error' && <FaDiscord />}
        </div>

        <h1 className="auth-cb__title">Conectar Discord</h1>
        <p className="auth-cb__message">{message}</p>

        {status === 'loading' && (
          <ul className="auth-cb__steps">
            <li>Identidade no Discord</li>
            <li>Vinculando à sua conta Nexus</li>
          </ul>
        )}

        {status === 'success' && userData && (
          <div className="auth-cb__user">
            <p className="auth-cb__user-name">
              {userData.global_name || userData.username}
              {userData.discriminator && userData.discriminator !== '0'
                ? `#${userData.discriminator}`
                : ''}
            </p>
            <p className="auth-cb__user-copy">{successCopy}</p>
          </div>
        )}

        {status === 'error' && (
          <div className="auth-cb__error">
            <ul>
              <li>Inicie o login novamente pela página de destino</li>
              <li>Certifique-se de autorizar o app no Discord</li>
            </ul>
            <button type="button" className="auth-cb__btn" onClick={handleTryAgain}>
              Tentar novamente
            </button>
          </div>
        )}

        <p className="auth-cb__footer">
          Precisa de ajuda?{' '}
          <a
            href="https://discord.gg/9W4Vusc4m2"
            target="_blank"
            rel="noopener noreferrer"
          >
            Entre no Discord
          </a>
        </p>
      </main>
    </div>
  );
};

export default AuthCallback;
